/**
 * État d'accès renvoyé par la RPC `get_exercise_v2_for_student` (migration 0115).
 * `allowed=false` ⇒ le contenu est tronqué à `free_questions` ; le client affiche
 * le paywall à la place des questions suivantes.
 */
export interface V2Access {
  allowed: boolean;
  reason?: string;
  chapter_type?: 'normal' | 'epreuves';
  free_questions?: number;
  total_questions?: number;
  expires_at?: string | null;
  /** 'skillcard' = exercice relié à une SKILLCARD (migration 0304). */
  product?: 'skillcard' | null;
  /** Carte principale de l'exercice (page d'achat). */
  skillcard_id?: string | null;
  /** L'exercice a des questions gratuites (essai gratuit, exercice non découpé). */
  free_sample?: boolean;
  deck_expires_at?: string | null;
}

/** Paywall SKILLCARD (achat carte / pack / programme) plutôt que le paywall pass. */
export function isSkillcardPaywall(access: V2Access | null | undefined): boolean {
  return !!access && access.product === 'skillcard' && !!access.skillcard_id;
}

/**
 * Vrai si l'exercice a du contenu verrouillé derrière le paywall : l'élève n'a
 * pas l'accès ET il reste des questions au-delà de la zone gratuite. Si l'exo
 * tient entièrement dans la zone gratuite (total ≤ free), pas de paywall — il
 * le termine normalement.
 */
export function isExerciseLocked(access: V2Access | null | undefined): boolean {
  if (!access || access.allowed) return false;
  const total = access.total_questions ?? 0;
  const free = access.free_questions ?? 0;
  return total > free;
}

/**
 * Partie payante d'un exercice de SKILLCARD découpé (migration 0501) sans
 * accès : AUCUNE question gratuite. L'exercice est verrouillé d'emblée — pas de
 * tentative, le paywall remplace l'écran d'accueil.
 */
export function isLockedUpfront(access: V2Access | null | undefined): boolean {
  return (
    !!access &&
    !access.allowed &&
    (access.free_questions ?? 1) === 0 &&
    (access.total_questions ?? 0) > 0
  );
}

/** Questions encore verrouillées après la zone gratuite (0 si tout est ouvert). */
export function remainingLockedQuestions(access: V2Access | null | undefined): number {
  if (!isExerciseLocked(access)) return 0;
  return Math.max(0, (access?.total_questions ?? 0) - (access?.free_questions ?? 0));
}

/**
 * Fin des questions gratuites d'un exercice relié à une SKILLCARD : l'exercice
 * est considéré TERMINÉ (bilan + `finish_attempt` → XP élève et HORA crédités),
 * l'offre d'achat s'affiche sous le bilan. Les autres paywalls (pass) gardent
 * la tentative `in_progress` pour la reprendre après achat.
 */
export function endsWithSkillcardSample(access: V2Access | null | undefined): boolean {
  return isExerciseLocked(access) && isSkillcardPaywall(access);
}
